<template>
  <nav class="navbar" :class="{ '-light': lightMode }">
    <Transition name="fade">
      <div class="navbar__top" v-if="isAtTop">
        <div class="navbar__top-left">
          <LangRouterLink :to="{ name: 'home' }" class="header__logo" v-reveal>
            <img src="@/assets/logo.svg" alt="" />
          </LangRouterLink>
          <a
            :href="$beet.options.navigation.address.link"
            target="_blank"
            class="header__top-element"
            v-reveal="{ delay: 200 }"
          >
            <p class="text__accent">[ {{ $beet.options.navigation.address.top }} ]</p>
            <p class="text">{{ $beet.options.navigation.address.bottom }}</p>
          </a>
        </div>
        <HighlightedTitle
          :title="$beet.options.navigation.quote"
          class="navbar__quote text__highlight"
          v-reveal="{ delay: 250 }"
        />
      </div>
    </Transition>
    <div class="navbar__bottom" ref="nav">
      <LangRouterLink
        :to="{ name: 'home' }"
        :class="['navbar__icon', { light: lightMode }, { '-visible': !isAtTop }]"
        @click="scrollTopSafety"
      >
        <Icon id="logo" />
      </LangRouterLink>
      <a
        :href="$beet.options.navigation.booking.link"
        target="_blank"
        :class="['button', 'navbar__button', { '-light': lightMode }]"
        v-reveal="{ delay: 300 }"
      >
        {{ $beet.options.navigation.booking.label }}
      </a>
      <a
        v-if="$beet.options.navigation.giftCard.link"
        :href="$beet.options.navigation.giftCard.link"
        target="_blank"
        :class="['button', 'navbar__button -secondary', { '-light': lightMode }]"
        v-reveal="{ delay: 325 }"
      >
        {{ $beet.options.navigation.giftCard.label }}
      </a>
      <ul class="navbar__links" v-reveal="{ delay: 350 }">
        <li v-for="(menuKey, menuKeyIndex) in $store.getters.menu" :key="menuKeyIndex">
          <LangRouterLink
            v-if="!$beet.options.navigation.menu[menuKey]['link']"
            :to="{ name: 'home', hash: `#${menuKey}` }"
            :class="[
              'navbar__link',
              { '-light': lightMode, '-current': currentSection === menuKey },
            ]"
          >
            {{ $beet.options.navigation.menu[menuKey]['label'] }}
          </LangRouterLink>
          <a
            v-if="$beet.options.navigation.menu[menuKey]['link']"
            :href="$beet.options.navigation.menu[menuKey]['link']"
            :class="[
              'navbar__link',
              { '-light': lightMode, '-current': currentSection === menuKey },
            ]"
            target="_blank"
          >
            {{ $beet.options.navigation.menu[menuKey]['label'] }}
          </a>
        </li>
        <li>
          <LangSwitch />
        </li>
      </ul>
    </div>
  </nav>
</template>
<script>
import Icon from '@/components/global/Icon.vue'
import LangRouterLink from '@/components/global/LangRouterLink.vue'
import HighlightedTitle from '@/components/small/HighlightedTitle.vue'
import LangSwitch from '@/components/small/LangSwitch.vue'
import HasScrollTopButton from '@/mixins/HasScrollTopButton'

export default {
  name: 'DesktopNav',
  mixins: [HasScrollTopButton],
  components: {
    Icon,
    LangRouterLink,
    HighlightedTitle,
    LangSwitch,
  },
  data() {
    return {
      isAtTop: true,
      isAtBottom: false,
      currentSection: null,
      lightMode: false,
      observer: null,
      observerRetry: null,
      menuOpened: false,
      sections: [],
    }
  },
  mounted() {
    window.addEventListener('scroll', this.updateIsAtTopState, { passive: true })
    window.addEventListener('resize', this.scheduleScrollObserver, { passive: true })
  },
  unmounted() {
    window.removeEventListener('scroll', this.updateIsAtTopState)
    window.removeEventListener('resize', this.scheduleScrollObserver)
    this.resetData()
  },
  methods: {
    resetData() {
      this.observer?.disconnect()
      window.clearTimeout(this.observerRetry)
      this.currentSection = null
      this.isAtTop = window.scrollY <= 20
      this.isAtBottom = false
      this.lightMode = false
      this.observer = null
      this.observerRetry = null
      this.sections = []
    },
    scheduleScrollObserver(delay = 100) {
      window.clearTimeout(this.observerRetry)
      this.observerRetry = window.setTimeout(
        this.setScrollObserver,
        typeof delay === 'number' ? delay : 100,
      )
    },
    setScrollObserver() {
      this.observer?.disconnect()
      this.sections = [
        ...document.querySelectorAll('section[data-light-menu]'),
        document.querySelector('footer'),
      ].filter(Boolean)

      if (this.sections.length <= 1) {
        this.scheduleScrollObserver()
        return
      }

      const probe = Math.round(window.innerHeight * 0.45)
      this.observer = new IntersectionObserver(this.updateActiveSection, {
        rootMargin: `-${probe}px 0px -${Math.max(window.innerHeight - probe - 1, 0)}px 0px`,
      })
      this.sections.forEach((section) => this.observer.observe(section))
      this.updateActiveSection()
    },
    updateActiveSection() {
      const probe = window.innerHeight * 0.45
      const target = this.isAtBottom
        ? this.sections.at(-1)
        : (this.sections.find((section) => {
            const bounds = section.getBoundingClientRect()
            return bounds.top <= probe && bounds.bottom > probe
          }) ??
          this.sections
            .toReversed()
            .find((section) => section.getBoundingClientRect().top <= probe))

      if (!target) return

      this.updateLightMode(target)
      this.updateCurrentSection(target)
    },
    updateCurrentSection(target) {
      this.currentSection = target.dataset.section
    },
    updateIsAtTopState() {
      this.isAtTop = window.scrollY <= 20
      const isAtBottom =
        window.scrollY > 0 &&
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2

      if (isAtBottom !== this.isAtBottom) {
        this.isAtBottom = isAtBottom
        this.updateActiveSection()
      }
    },
    updateLightMode(target) {
      this.lightMode = target.dataset.lightMenu === 'true'
    },
  },
  watch: {
    '$route.name': {
      handler() {
        this.resetData()
        this.scheduleScrollObserver(250)
      },
      immediate: true,
    },
  },
}
</script>
